"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Film, Building2, Palette, Music2, Code2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { ThemeSection, ThemeText, AuroraBlobs } from "@/components/ui/theme-utils";

type Case = {
  id: string;
  industry: string;
  icon: React.ElementType;
  title: string;
  description: string;
  highlights: string[];
};

const CASES: Case[] = [
  {
    id: "film",
    industry: "Film & Video",
    icon: Film,
    title: "Send the dailies without the dread",
    description:
      "Production studios move terabytes a day. Zset splits 4K/6K/8K RAW into parallel chunks and ships them at line rate, even on shaky uplink.",
    highlights: [
      "Optimized for ProRes / BRAW / R3D",
      "Mid-file resume across shoot days",
      "Watermarked preview links"
    ]
  },
  {
    id: "photo",
    industry: "Photography",
    icon: Camera,
    title: "Galleries your client can actually open",
    description:
      "Upload 80 GB weddings, deliver branded download pages, lock them when the contract closes.",
    highlights: [
      "Auto-generated gallery pages",
      "Client-facing download portal",
      "Per-shoot access expiry"
    ]
  },
  {
    id: "enterprise",
    industry: "Enterprise",
    icon: Building2,
    title: "Replace FTP without a procurement project",
    description:
      "SSO, audit trails, and per-team quotas — drop Zset in front of the rest of your stack in a single afternoon.",
    highlights: ["SSO + SCIM", "SOC 2-ready controls", "Per-team quotas & RBAC"]
  },
  {
    id: "creative",
    industry: "Creative agencies",
    icon: Palette,
    title: "Files in. Approvals out.",
    description:
      "Clients comment, you ship the next cut. No more Drive confusion, no more 'final_v7_actuallyfinal.psd'.",
    highlights: [
      "Comment threads on assets",
      "Version history",
      "Lock the room when shipped"
    ]
  },
  {
    id: "audio",
    industry: "Music & Audio",
    icon: Music2,
    title: "Lossless stems, no transcode",
    description:
      "Push 24-bit/192 kHz masters and multitrack sessions with bit-perfect integrity checks.",
    highlights: [
      "WAV / FLAC / AIFF native",
      "Hash-verified delivery",
      "Project bundles with stems"
    ]
  },
  {
    id: "dev",
    industry: "Engineering",
    icon: Code2,
    title: "Big repos, small pipelines",
    description:
      "CI artifacts, model weights, dataset snapshots — share them with your team without clogging email or GitHub Releases.",
    highlights: [
      "S3-compatible endpoints",
      "Pre-signed short-TTL links",
      "Webhooks on delivery"
    ]
  }
];

export function UseCases() {
  const { isDark } = useTheme();
  const [active, setActive] = useState(CASES[0].id);
  const current = CASES.find((c) => c.id === active) ?? CASES[0];

  return (
    <ThemeSection id="use-cases" pattern="dots">
      <AuroraBlobs variant="mixed" />
      <div className="container mx-auto px-5 sm:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium mb-5",
              isDark
                ? "bg-white/[0.04] border border-white/10 text-white/80"
                : "bg-white border border-stone-200 text-stone-700"
            )}
          >
            <Palette className="h-3.5 w-3.5 text-brand-500" />
            Built for every team that ships big
          </span>
          <h2 className="responsive-heading-2 mb-5">
            One transfer layer,{" "}
            <span className="text-gradient">six industries</span>
          </h2>
          <ThemeText variant="subtle" className="responsive-text-lg">
            Whatever moves your team — pixels, stems, models, builds — Zset
            carries it.
          </ThemeText>
        </div>

        <div
          className={cn(
            "rounded-3xl p-2 sm:p-3 shadow-elevated",
            isDark
              ? "bg-white/[0.02] border border-white/[0.08]"
              : "bg-white/80 border border-stone-200/80 backdrop-blur"
          )}
        >
          {/* Tabs */}
          <div
            className={cn(
              "flex flex-wrap gap-1 p-1.5 rounded-2xl mb-2",
              isDark ? "bg-white/[0.03]" : "bg-stone-100/80"
            )}
          >
            {CASES.map((c) => {
              const Icon = c.icon;
              const isActive = c.id === active;
              return (
                <button
                  key={c.id}
                  onClick={() => setActive(c.id)}
                  className={cn(
                    "relative inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-colors flex-1 justify-center",
                    isActive
                      ? isDark
                        ? "text-white"
                        : "text-stone-900"
                      : isDark
                      ? "text-white/60 hover:text-white/90"
                      : "text-stone-600 hover:text-stone-900"
                  )}
                >
                  {isActive && (
                    <motion.span
                      layoutId="use-case-pill"
                      className={cn(
                        "absolute inset-0 rounded-xl shadow-sm",
                        isDark ? "bg-white/10" : "bg-white"
                      )}
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  <Icon className="h-4 w-4 relative z-10" />
                  <span className="relative z-10 hidden sm:inline">
                    {c.industry}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Panel */}
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className={cn(
                "rounded-2xl p-6 sm:p-10",
                isDark
                  ? "bg-gradient-to-br from-white/[0.04] to-white/[0.01]"
                  : "bg-gradient-to-br from-stone-50 to-white"
              )}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider mb-4",
                      "bg-gradient-to-r from-brand-700/15 to-brand-900/15 text-brand-700 dark:text-brand-200 border border-brand-700/20"
                    )}
                  >
                    {current.industry}
                  </span>
                  <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
                    {current.title}
                  </h3>
                  <ThemeText variant="subtle" className="text-base sm:text-lg">
                    {current.description}
                  </ThemeText>
                </div>
                <ul className="space-y-2.5">
                  {current.highlights.map((h) => (
                    <li
                      key={h}
                      className={cn(
                        "flex items-center gap-3 rounded-2xl px-4 py-3",
                        isDark
                          ? "bg-white/[0.03] border border-white/5"
                          : "bg-white border border-stone-200/80"
                      )}
                    >
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white text-xs">
                        ✓
                      </span>
                      <span className="text-sm font-medium">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </ThemeSection>
  );
}
